/**
 * Setting an institution's own name — the one string on this platform whose
 * length nobody controls.
 *
 * Tenant names here run from "UNIKIN" (6 characters) to "Université Nationale
 * des Sciences, Technologies, Ingénierie et Mathématiques" (75), and the
 * markets this product sells into favour the long end: francophone West and
 * Central African institutions routinely spell out every discipline they
 * cover. A fixed type size is therefore wrong by construction — it is chosen
 * for one name and every other tenant lives with the consequences. UNSTIM's
 * name set at the portal's `h3` filled four lines and pushed the sign-in form
 * off the fold.
 *
 * Two rules, applied together, because either alone still fails:
 *
 *   1. WRAP. A name must be allowed to occupy the lines it needs; clipping an
 *      institution's name with an ellipsis is worse than a small one.
 *   2. SHRINK. Wrapping alone turns a long name into a wall of display type
 *      that shouts louder than the short one it sits beside. The size comes
 *      down as the name gets longer, so every tenant's masthead occupies
 *      roughly the same AREA rather than the same line height.
 *
 * The shrink is continuous, not stepped, and derived rather than tabulated —
 * a table of "if longer than N, use size X" is a set of magic numbers that
 * nobody can extend to the next tenant.
 */

/**
 * Characters that comfortably fill ONE line at a heading's full size.
 *
 * Measured against the portal hero rather than guessed: at `h3` in the serif
 * display face, a centred hero column fits a little over twenty characters
 * before it wraps. Names at or under this keep the full size — "Université de
 * Kinshasa" (22) is unchanged by any of this, which is the point: the scale
 * must be inert for the names it was already right for.
 */
const ONE_LINE_CHARS = 22;

/**
 * How far the size falls as the name grows.
 *
 * SQUARE ROOT, not a straight ratio. Text occupies area, and a name twice as
 * long set at half the size would render at a QUARTER of the visual weight —
 * legible, but so much smaller than a short tenant's name that the two portals
 * no longer look like the same product. `sqrt` keeps the occupied area roughly
 * constant instead: double the characters, about 1.4× smaller type, over two
 * lines rather than one.
 */
export function fitNameSize(name, max, min) {
  const len = String(name || "").trim().length;
  if (!len || len <= ONE_LINE_CHARS) return max;
  const scaled = max * Math.sqrt(ONE_LINE_CHARS / len);
  // Two decimals: rem values below that are sub-pixel at every size we set.
  return Math.max(min, Math.round(scaled * 100) / 100);
}

/**
 * Wrapping rules for a heading holding a name.
 *
 * `overflowWrap: break-word` is the floor under everything else — it only
 * breaks a word that cannot fit on a line of its own, which is the case a
 * shrink cannot rescue ("Wirtschaftswissenschaften", or a URL-like token in a
 * mis-typed name). Without it such a name draws straight through the edge of
 * its panel.
 *
 * `textWrap: balance` evens the lines out, so a two-line name reads as a
 * masthead rather than as a full line with one orphaned word beneath it.
 * Browsers that don't implement it ignore the declaration and wrap normally,
 * and browsers that do cap it at a few lines — both are fine, it is polish on
 * top of a layout that already works.
 */
export const NAME_WRAP_SX = {
  overflowWrap: "break-word",
  textWrap: "balance",
  maxWidth: "100%",
};

/**
 * Everything a heading needs to hold a name of any length.
 *
 * @param {string} name
 * @param {Object<string, [number, number]>} ramp  MUI breakpoint → [maxRem, minRem]
 * @returns {object} an MUI `sx` fragment — spread it, don't replace the caller's own
 *
 * The ramp is per breakpoint because the space available for a name is not a
 * fixed fraction of the viewport: the portal hero is half the screen on a
 * desktop and all of it on a phone. Each entry names the size that breakpoint
 * would have used for a SHORT name (so nothing changes for the tenants this
 * was already right for) and the floor below which the name stops reading as
 * the institution's own.
 */
export function nameSx(name, ramp) {
  return { ...NAME_WRAP_SX, fontSize: nameFontSize(name, ramp) };
}

/**
 * Just the responsive `fontSize`, for a heading that must NOT wrap.
 *
 * The app bar is the case: it is one fixed-height line, so it keeps its own
 * `noWrap`/ellipsis and takes only the scale. Spreading the wrap rules there
 * and then overriding them with `white-space: nowrap` would leave two
 * contradictory declarations for the next reader to untangle.
 */
export function nameFontSize(name, ramp) {
  const fontSize = {};
  for (const [bp, [max, min]] of Object.entries(ramp)) {
    fontSize[bp] = `${fitNameSize(name, max, min)}rem`;
  }
  return fontSize;
}

/**
 * The longest form of an institution's name that fits the space on offer.
 *
 * For CHROME — the app bar, a breadcrumb, a tab — where the name identifies
 * which tenant you are in rather than making the institutional statement the
 * portal does. There, a name too long for one line has only bad options:
 * ellipsis truncates it to "Université Nationale des Scien…", which names no
 * institution in particular, and wrapping grows a fixed-height bar.
 *
 * So prefer the name, and fall back to the abbreviation the institution chose
 * for itself — never to a machine-made one, which is how a product invents an
 * acronym its customer does not use. `full` is returned alongside so the
 * caller can put the complete name in a tooltip; `abbreviated` says whether it
 * needs to bother.
 */
export function institutionLabel(university, maxChars = 34) {
  const name = String(university?.name || "").trim();
  const short = String(university?.shortName || "").trim();
  if (!name) return { label: short, full: short, abbreviated: false };
  if (name.length <= maxChars || !short) {
    return { label: name, full: name, abbreviated: false };
  }
  return { label: short, full: name, abbreviated: true };
}
